import { Badge } from "@design";
import { useTranslation } from "react-i18next";
import type { WikiFileSummary } from "@/api";
import { Text } from "@/components/Text";
import { isNodeType } from "./nodeTypes";
import { VisibilityBadge } from "./VisibilityBadge";

interface Group {
  folder: string;
  files: WikiFileSummary[];
}

/** Files grouped by folder; root files first, folders alphabetically. */
export function groupByFolder(files: WikiFileSummary[]): Group[] {
  const groups = new Map<string, WikiFileSummary[]>();
  for (const file of files) {
    const cut = file.path.lastIndexOf("/");
    const folder = cut === -1 ? "" : file.path.slice(0, cut);
    groups.set(folder, [...(groups.get(folder) ?? []), file]);
  }
  return [...groups.entries()].sort(([a], [b]) => byFolder(a, b)).map(([folder, items]) => ({ folder, files: items }));
}

/** Top-level files (folder "") first, then folders A to Z. */
function byFolder(a: string, b: string): number {
  if (a === b) return 0;
  if (a === "") return -1;
  if (b === "") return 1;
  return a.localeCompare(b);
}

/** The name a file is listed under: its title, else its file name. */
export function fileLabel(file: Pick<WikiFileSummary, "title" | "path">): string {
  return file.title ?? file.path.slice(file.path.lastIndexOf("/") + 1).replace(/\.md$/i, "");
}

// anthm-design ListItem has no selected/current state yet (tracked for design); this
// row mirrors its look with aria-current until it does. The current row uses
// the same treatment as the app's ListNav (elevated surface + left bar).
const CURRENT_ROW =
  "relative bg-surface-elevated shadow-sm before:absolute before:inset-y-1.5 before:left-0 before:w-0.5 before:rounded-full before:bg-content-primary";
function FileRow({
  file,
  current,
  onSelect,
}: Readonly<{ file: WikiFileSummary; current: boolean; onSelect: () => void }>) {
  const { t } = useTranslation("knowledge");
  const issues = file.errorCount + file.warningCount;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={current ? "page" : undefined}
      className={`btn-focus-ring flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors hover:bg-interactive-hover ${current ? CURRENT_ROW : ""}`}
    >
      <span className="min-w-0 flex-1">
        <Text variant="body-sm" className="block truncate">
          {fileLabel(file)}
        </Text>
        <Text variant="caption" tone="secondary" className="block truncate">
          {isNodeType(file.nodeType) ? t(`type.${file.nodeType}`) : t("type.none")}
        </Text>
      </span>
      {issues > 0 && (
        <Badge
          dot
          variant={file.errorCount > 0 ? "danger" : "warning"}
          aria-label={t("list.issues", { count: issues })}
        />
      )}
      <VisibilityBadge visibility={file.visibility} />
    </button>
  );
}

export function WikiFileList({
  files,
  selected,
  onSelect,
}: Readonly<{ files: WikiFileSummary[]; selected: string | null; onSelect: (path: string) => void }>) {
  const { t } = useTranslation("knowledge");
  return (
    <nav aria-label={t("pages")} className="flex flex-col gap-4">
      {groupByFolder(files).map((group) => (
        <section key={group.folder || "/"} className="flex flex-col gap-1">
          {group.folder && (
            <Text variant="eyebrow" tone="tertiary" as="h2" className="px-3">
              {group.folder}
            </Text>
          )}
          <ul className="flex flex-col gap-0.5">
            {group.files.map((file) => (
              <li key={file.path}>
                <FileRow file={file} current={file.path === selected} onSelect={() => onSelect(file.path)} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </nav>
  );
}
